import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ShieldCheck, KeyRound, Snowflake, ExternalLink, Trash2, Plus, RefreshCw,
  ClipboardCheck, AlertTriangle, Copy, Info, Sparkles, FileDown, Sparkle, Brush,
  ClipboardList, X,
} from 'lucide-react';

const WEEKDAY_LABEL = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function HealthSafetyPage() {
  const { api, currentLocation } = useAuth();
  const [tab, setTab] = useState('pins');
  const [pins, setPins] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [assets, setAssets] = useState([]);
  const [cleaningTasks, setCleaningTasks] = useState([]);
  const [countSheets, setCountSheets] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(null);

  // Pin dialog
  const [pinDialog, setPinDialog] = useState(false);
  const [pinForm, setPinForm] = useState({ employee_id: '', pin: '', is_manager: false });

  // Asset dialog
  const [assetDialog, setAssetDialog] = useState(false);
  const [assetForm, setAssetForm] = useState({ kind: 'fridge', name: '', target_min_c: 1, target_max_c: 5 });

  // Cleaning task dialog
  const [taskDialog, setTaskDialog] = useState(false);
  const [taskForm, setTaskForm] = useState({ name: '', schedule: 'daily', weekday: 0, day_of_month: 1 });
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Count sheet dialog
  const [sheetDialog, setSheetDialog] = useState(false);
  const [sheetForm, setSheetForm] = useState({ name: '', item_ids: [] });

  const kioskUrl = useMemo(() => {
    if (!config?.kiosk_slug) return '';
    return `${window.location.origin}/kiosk/${config.kiosk_slug}`;
  }, [config]);

  const load = useCallback(async () => {
    if (!currentLocation) return;
    setLoading(true);
    try {
      const [pinsRes, empRes, assetsRes, tasksRes, cfgRes, histRes, sheetsRes, invRes] = await Promise.all([
        api.get('/health-safety/pins'),
        api.get('/employees', { params: { location_id: currentLocation.id } }).catch(() => ({ data: [] })),
        api.get('/health-safety/assets', { params: { location_id: currentLocation.id } }),
        api.get('/health-safety/cleaning-tasks', { params: { location_id: currentLocation.id } }),
        api.get(`/health-safety/kiosk-config/${currentLocation.id}`),
        api.get('/health-safety/history', { params: { location_id: currentLocation.id, days: 28 } }),
        api.get('/health-safety/count-sheets', { params: { location_id: currentLocation.id } }),
        api.get('/inventory', { params: { location_id: currentLocation.id } }).catch(() => ({ data: { items: [] } })),
      ]);
      setPins(pinsRes.data);
      setEmployees(empRes.data);
      setAssets(assetsRes.data);
      setCleaningTasks(tasksRes.data);
      setConfig(cfgRes.data);
      setHistory(histRes.data);
      setCountSheets(sheetsRes.data);
      setInventoryItems(invRes.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load Health & Safety');
    } finally {
      setLoading(false);
    }
  }, [api, currentLocation]);

  useEffect(() => { load(); }, [load]);

  const savePin = async () => {
    if (!/^\d{4,8}$/.test(pinForm.pin)) return toast.error('PIN must be 4-8 digits');
    if (!pinForm.employee_id) return toast.error('Choose an employee');
    try {
      await api.post('/health-safety/pins', pinForm);
      toast.success('PIN saved');
      setPinDialog(false);
      setPinForm({ employee_id: '', pin: '', is_manager: false });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save PIN');
    }
  };

  const revokePin = async (id) => {
    if (!window.confirm('Revoke this PIN? The employee will need a new one to use the kiosk.')) return;
    try {
      await api.delete(`/health-safety/pins/${id}`);
      toast.success('PIN revoked');
      load();
    } catch (err) {
      toast.error('Failed to revoke');
    }
  };

  const saveAsset = async () => {
    if (!assetForm.name) return toast.error('Name required');
    if (Number(assetForm.target_min_c) >= Number(assetForm.target_max_c)) return toast.error('Min must be lower than Max');
    try {
      await api.post('/health-safety/assets', { ...assetForm, location_id: currentLocation.id });
      toast.success('Asset added');
      setAssetDialog(false);
      setAssetForm({ kind: 'fridge', name: '', target_min_c: 1, target_max_c: 5 });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const deleteAsset = async (id) => {
    if (!window.confirm('Delete this fridge/freezer? Historic readings are preserved.')) return;
    try {
      await api.delete(`/health-safety/assets/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed'); }
  };

  const saveTask = async () => {
    if (!taskForm.name.trim()) return toast.error('Name required');
    if ((taskForm.schedule === 'weekly' || taskForm.schedule === 'fortnightly') && (taskForm.weekday === null || taskForm.weekday === undefined)) {
      return toast.error('Pick a weekday');
    }
    if (taskForm.schedule === 'monthly' && !taskForm.day_of_month) {
      return toast.error('Pick a day of the month');
    }
    try {
      await api.post('/health-safety/cleaning-tasks', {
        location_id: currentLocation.id,
        name: taskForm.name.trim(),
        schedule: taskForm.schedule,
        weekday: (taskForm.schedule === 'weekly' || taskForm.schedule === 'fortnightly') ? Number(taskForm.weekday) : null,
        day_of_month: taskForm.schedule === 'monthly' ? Number(taskForm.day_of_month) : null,
      });
      toast.success('Cleaning task added');
      setTaskDialog(false);
      setTaskForm({ name: '', schedule: 'daily', weekday: 0, day_of_month: 1 });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const deleteTask = async (id) => {
    if (!window.confirm('Remove this cleaning task from the kiosk? Past completions stay in the record.')) return;
    try {
      await api.delete(`/health-safety/cleaning-tasks/${id}`);
      toast.success('Removed');
      load();
    } catch { toast.error('Failed'); }
  };

  const saveSheet = async () => {
    if (!sheetForm.name.trim()) return toast.error('Name required');
    try {
      await api.post('/health-safety/count-sheets', {
        location_id: currentLocation.id,
        name: sheetForm.name.trim(),
        item_ids: sheetForm.item_ids,
      });
      toast.success('Count sheet saved');
      setSheetDialog(false);
      setSheetForm({ name: '', item_ids: [] });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const deleteSheet = async (id) => {
    if (!window.confirm('Delete this count sheet? Past stock takes stay in the record.')) return;
    try {
      await api.delete(`/health-safety/count-sheets/${id}`);
      toast.success('Removed');
      load();
    } catch { toast.error('Failed'); }
  };

  const toggleSheetItem = (variationId) => {
    setSheetForm(f => ({
      ...f,
      item_ids: f.item_ids.includes(variationId)
        ? f.item_ids.filter(x => x !== variationId)
        : [...f.item_ids, variationId],
    }));
  };

  const downloadEhoPack = async (days) => {
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/health-safety/eho-pack/${currentLocation.id}`, {
        params: { days }, responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `eho-pack-${days}d.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('EHO pack downloaded');
    } catch (err) {
      toast.error('Failed to build PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const updateConfig = async (patch) => {
    try {
      await api.patch(`/health-safety/kiosk-config/${currentLocation.id}`, patch);
      toast.success('Kiosk settings updated');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(kioskUrl).then(() => toast.success('Kiosk URL copied — bookmark this on the iPad'));
  };

  return (
    <div className="p-6 space-y-6" data-testid="health-safety-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><ShieldCheck className="w-7 h-7" /> Health & Safety</h1>
          <p className="text-muted-foreground max-w-2xl">
            SFBB-style daily records for your café. Set up an iPad kiosk by the till so staff can log fridge temperatures and daily sign-offs with a 4-digit PIN.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="hs-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Kiosk URL banner */}
      {config?.kiosk_slug && (
        <Card className="border-primary/30 bg-primary/5" data-testid="kiosk-url-card">
          <CardContent className="py-4 flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Kiosk URL for {config.location_name}</p>
              <p className="font-mono text-sm break-all">{kioskUrl}</p>
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                <span>Bookmark this on the iPad by the till. Staff enter their PIN to start the day. Kiosk locks after {config.auto_lock_seconds}s of inactivity.</span>
                {config.strict_mode ? (
                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px]">Strict mode — sign-off blocked while corrective actions open</Badge>
                ) : (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Advisory (day {config.days_since_start}/28) — soft ramp-up</Badge>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={copyUrl} data-testid="copy-kiosk-url"><Copy className="w-3 h-3 mr-1" /> Copy URL</Button>
              <Button variant="outline" size="sm" onClick={() => window.open(kioskUrl, '_blank')} data-testid="open-kiosk"><ExternalLink className="w-3 h-3 mr-1" /> Open kiosk</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pins" data-testid="tab-pins"><KeyRound className="w-4 h-4 mr-1" /> Staff PINs</TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets"><Snowflake className="w-4 h-4 mr-1" /> Fridges & Freezers</TabsTrigger>
          <TabsTrigger value="cleaning" data-testid="tab-cleaning"><Brush className="w-4 h-4 mr-1" /> Cleaning</TabsTrigger>
          <TabsTrigger value="count-sheets" data-testid="tab-count-sheets"><ClipboardList className="w-4 h-4 mr-1" /> Count sheets</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><ClipboardCheck className="w-4 h-4 mr-1" /> Records (28d)</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings"><Info className="w-4 h-4 mr-1" /> Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="pins" className="space-y-3 mt-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Each employee gets a 4-8 digit PIN. Managers can sign off the day and close corrective actions.</p>
            <Button onClick={() => setPinDialog(true)} data-testid="add-pin-btn"><Plus className="w-4 h-4 mr-1" /> Add PIN</Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Manager?</TableHead>
                    <TableHead>Last used</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pins.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No PINs yet. Add one for each employee who needs kiosk access.</TableCell></TableRow>
                  ) : pins.map(p => (
                    <TableRow key={p.id} data-testid={`pin-row-${p.id}`}>
                      <TableCell>{p.employee_name || p.employee_id}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{p.employee_role || '—'}</TableCell>
                      <TableCell>{p.is_manager ? <Badge>Manager</Badge> : <Badge variant="outline">Staff</Badge>}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{p.last_used_at ? new Date(p.last_used_at).toLocaleString() : 'Never'}</TableCell>
                      <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => revokePin(p.id)} data-testid={`revoke-${p.id}`}><Trash2 className="w-3 h-3" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assets" className="space-y-3 mt-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Every fridge and freezer to be checked daily on the kiosk.</p>
            <Button onClick={() => setAssetDialog(true)} data-testid="add-asset-btn"><Plus className="w-4 h-4 mr-1" /> Add fridge/freezer</Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Target range</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assets.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No fridges/freezers configured yet.</TableCell></TableRow>
                  ) : assets.map(a => (
                    <TableRow key={a.id} data-testid={`asset-row-${a.id}`}>
                      <TableCell>{a.name}</TableCell>
                      <TableCell className="capitalize">{a.kind}</TableCell>
                      <TableCell>{a.target_min_c}°C to {a.target_max_c}°C</TableCell>
                      <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => deleteAsset(a.id)} data-testid={`del-asset-${a.id}`}><Trash2 className="w-3 h-3" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cleaning" className="space-y-3 mt-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Hourly, daily, weekly, fortnightly and monthly tasks. Weekly &amp; fortnightly tasks show on the kiosk only on their chosen weekday; monthly tasks show on their chosen day-of-month (or the last day if the month is shorter).</p>
            <Button onClick={() => setTaskDialog(true)} data-testid="add-task-btn"><Plus className="w-4 h-4 mr-1" /> Add cleaning task</Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Schedule</TableHead>
                    <TableHead>When</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cleaningTasks.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No tasks yet. Add e.g. "Wipe front counter" (daily) or "Deep clean fridges" (weekly Sun).</TableCell></TableRow>
                  ) : cleaningTasks.map(t => (
                    <TableRow key={t.id} data-testid={`task-row-${t.id}`}>
                      <TableCell>{t.name}</TableCell>
                      <TableCell className="capitalize">{t.schedule}</TableCell>
                      <TableCell>{(() => {
                        if (t.schedule === 'weekly' || t.schedule === 'fortnightly') {
                          return (t.weekday !== null && t.weekday !== undefined) ? WEEKDAY_LABEL[t.weekday] : '—';
                        }
                        if (t.schedule === 'monthly') {
                          return t.day_of_month ? `Day ${t.day_of_month}` : '—';
                        }
                        return '—';
                      })()}</TableCell>
                      <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => deleteTask(t.id)} data-testid={`del-task-${t.id}`}><Trash2 className="w-3 h-3" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="count-sheets" className="space-y-3 mt-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Predefined lists staff pick on the kiosk to run a stock take (e.g. "Morning fridge count", "End of day dry stock").</p>
            <Button onClick={() => setSheetDialog(true)} data-testid="add-sheet-btn"><Plus className="w-4 h-4 mr-1" /> Add count sheet</Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sheet</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Last done</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {countSheets.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No count sheets yet. Kiosk staff can still run Free counts.</TableCell></TableRow>
                  ) : countSheets.map(s => (
                    <TableRow key={s.id} data-testid={`sheet-row-${s.id}`}>
                      <TableCell>{s.name}</TableCell>
                      <TableCell>{s.item_count} items</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{s.last_completed_at ? new Date(s.last_completed_at).toLocaleString() : 'Never'}</TableCell>
                      <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => deleteSheet(s.id)} data-testid={`del-sheet-${s.id}`}><Trash2 className="w-3 h-3" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-3 mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Recent records — last 28 days</CardTitle>
                <CardDescription>The audit trail an EHO would want to see. Click a button below to download the full pack.</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => downloadEhoPack(7)} disabled={downloadingPdf} data-testid="eho-7"><FileDown className="w-3 h-3 mr-1" />7d</Button>
                <Button size="sm" variant="outline" onClick={() => downloadEhoPack(28)} disabled={downloadingPdf} data-testid="eho-28"><FileDown className="w-3 h-3 mr-1" />28d</Button>
                <Button size="sm" onClick={() => downloadEhoPack(90)} disabled={downloadingPdf} data-testid="eho-90"><FileDown className="w-3 h-3 mr-1" />EHO pack (90d)</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-medium text-sm mb-2">Temperature checks ({history?.checks?.length || 0})</h3>
                {history?.checks?.length ? (
                  <Table>
                    <TableHeader>
                      <TableRow><TableHead>When</TableHead><TableHead>Asset</TableHead><TableHead>Reading</TableHead><TableHead>Result</TableHead><TableHead>By</TableHead></TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.checks.slice(0, 20).map(c => (
                        <TableRow key={c.id}>
                          <TableCell className="text-sm">{new Date(c.created_at).toLocaleString()}</TableCell>
                          <TableCell>{c.asset_name}</TableCell>
                          <TableCell>{c.reading_c}°C</TableCell>
                          <TableCell>{c.passed ? <Badge className="bg-green-500">Pass</Badge> : <Badge variant="destructive">Fail</Badge>}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{c.employee_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : <p className="text-sm text-muted-foreground">No checks yet.</p>}
              </div>
              <div>
                <h3 className="font-medium text-sm mb-2">Corrective actions ({history?.corrective_actions?.length || 0})</h3>
                {history?.corrective_actions?.length ? (
                  <div className="space-y-2">
                    {history.corrective_actions.slice(0, 10).map(ca => (
                      <div key={ca.id} className="border rounded p-2 text-sm">
                        <div className="flex justify-between">
                          <span>{ca.description}</span>
                          <Badge variant={ca.status === 'open' ? 'destructive' : 'outline'}>{ca.status}</Badge>
                        </div>
                        {ca.reason && <p className="text-xs text-muted-foreground mt-1">{ca.reason}</p>}
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">None open.</p>}
              </div>
              <div>
                <h3 className="font-medium text-sm mb-2">Daily sign-offs ({history?.signoffs?.length || 0})</h3>
                {history?.signoffs?.length ? (
                  <div className="space-y-1 text-sm">
                    {history.signoffs.map(s => (
                      <div key={s.id} className="flex justify-between border-b py-1">
                        <span>{s.date}</span>
                        <span className="text-muted-foreground">Signed by {s.signed_by_name}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">No sign-offs yet.</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-3 mt-4">
          {config && (
            <Card>
              <CardHeader>
                <CardTitle>Kiosk behaviour</CardTitle>
                <CardDescription>Fine-tune how the iPad kiosk behaves.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 max-w-md">
                <div>
                  <Label>Auto-lock after inactivity (seconds)</Label>
                  <Input type="number" min="60" max="3600" defaultValue={config.auto_lock_seconds}
                    onBlur={(e) => Number(e.target.value) !== config.auto_lock_seconds && updateConfig({ auto_lock_seconds: Number(e.target.value) })}
                    data-testid="auto-lock-input" />
                </div>
                <div>
                  <Label>Corrective-action gating</Label>
                  <Select value={config.strict_mode_override || 'auto'} onValueChange={(v) => updateConfig({ strict_mode_override: v === 'auto' ? null : v })}>
                    <SelectTrigger data-testid="strict-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Automatic (advisory days 1-28, strict from day 29)</SelectItem>
                      <SelectItem value="advisory">Force advisory (never blocks sign-off)</SelectItem>
                      <SelectItem value="strict">Force strict (always blocks sign-off if CAs open)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Currently: <b>{config.strict_mode ? 'STRICT' : 'ADVISORY'}</b> (day {config.days_since_start} of ramp-up).
                  </p>
                </div>
                <div>
                  <Label>Who can start a stock take</Label>
                  <Select value={config.stock_take_start_permission || 'any'} onValueChange={(v) => updateConfig({ stock_take_start_permission: v })}>
                    <SelectTrigger data-testid="stock-start-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any signed-in PIN</SelectItem>
                      <SelectItem value="manager">Managers only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Who can finish &amp; approve a stock take</Label>
                  <Select value={config.stock_take_approve_permission || 'manager'} onValueChange={(v) => updateConfig({ stock_take_approve_permission: v })}>
                    <SelectTrigger data-testid="stock-approve-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any signed-in PIN</SelectItem>
                      <SelectItem value="manager">Managers only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Push stock takes to POS</Label>
                  <Select value={config.stock_take_push_mode || 'on_approval'} onValueChange={(v) => updateConfig({ stock_take_push_mode: v })}>
                    <SelectTrigger data-testid="stock-push-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediate">Immediately on each line save</SelectItem>
                      <SelectItem value="on_approval">On approval (batch)</SelectItem>
                      <SelectItem value="never">Never — Tillytics-only</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    If the connected POS can't accept inventory writes, movements are kept in Tillytics automatically.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Pin dialog */}
      <Dialog open={pinDialog} onOpenChange={setPinDialog}>
        <DialogContent data-testid="pin-dialog">
          <DialogHeader><DialogTitle>Add staff PIN</DialogTitle><DialogDescription>4-8 digits. Stored hashed at rest.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Employee</Label>
              <Select value={pinForm.employee_id} onValueChange={(v) => setPinForm(f => ({ ...f, employee_id: v }))}>
                <SelectTrigger data-testid="pin-emp-select"><SelectValue placeholder="Choose employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} — {e.role || 'staff'}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>PIN</Label>
              <Input type="password" inputMode="numeric" maxLength={8} value={pinForm.pin}
                onChange={(e) => setPinForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, '') }))}
                data-testid="pin-input" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={pinForm.is_manager} onCheckedChange={(v) => setPinForm(f => ({ ...f, is_manager: v }))} data-testid="pin-manager-switch" />
              <Label>Manager (can sign off day + close corrective actions)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPinDialog(false)}>Cancel</Button>
            <Button onClick={savePin} data-testid="save-pin-btn">Save PIN</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Asset dialog */}
      <Dialog open={assetDialog} onOpenChange={setAssetDialog}>
        <DialogContent data-testid="asset-dialog">
          <DialogHeader><DialogTitle>Add fridge or freezer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={assetForm.kind} onValueChange={(v) => setAssetForm(f => ({ ...f, kind: v, target_min_c: v === 'freezer' ? -22 : 1, target_max_c: v === 'freezer' ? -18 : 5 }))}>
                <SelectTrigger data-testid="asset-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fridge">Fridge</SelectItem>
                  <SelectItem value="freezer">Freezer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Name</Label>
              <Input value={assetForm.name} onChange={(e) => setAssetForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Under-counter fridge #1" data-testid="asset-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Target min °C</Label><Input type="number" step="0.1" value={assetForm.target_min_c} onChange={(e) => setAssetForm(f => ({ ...f, target_min_c: parseFloat(e.target.value) }))} data-testid="asset-min-input" /></div>
              <div><Label>Target max °C</Label><Input type="number" step="0.1" value={assetForm.target_max_c} onChange={(e) => setAssetForm(f => ({ ...f, target_max_c: parseFloat(e.target.value) }))} data-testid="asset-max-input" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssetDialog(false)}>Cancel</Button>
            <Button onClick={saveAsset} data-testid="save-asset-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Cleaning task dialog */}
      <Dialog open={taskDialog} onOpenChange={setTaskDialog}>
        <DialogContent data-testid="task-dialog">
          <DialogHeader><DialogTitle>Add cleaning task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Task name</Label>
              <Input value={taskForm.name} onChange={(e) => setTaskForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Wipe front counter" data-testid="task-name-input" />
            </div>
            <div>
              <Label>How often?</Label>
              <Select value={taskForm.schedule} onValueChange={(v) => setTaskForm(f => ({ ...f, schedule: v }))}>
                <SelectTrigger data-testid="task-schedule-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="fortnightly">Fortnightly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(taskForm.schedule === 'weekly' || taskForm.schedule === 'fortnightly') && (
              <div>
                <Label>Which day?</Label>
                <Select value={String(taskForm.weekday)} onValueChange={(v) => setTaskForm(f => ({ ...f, weekday: Number(v) }))}>
                  <SelectTrigger data-testid="task-weekday-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WEEKDAY_LABEL.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
                {taskForm.schedule === 'fortnightly' && (
                  <p className="text-xs text-muted-foreground mt-1">Appears every other {WEEKDAY_LABEL[taskForm.weekday]} on the kiosk, starting the week after this task is created.</p>
                )}
              </div>
            )}
            {taskForm.schedule === 'monthly' && (
              <div>
                <Label>Day of month</Label>
                <Select value={String(taskForm.day_of_month)} onValueChange={(v) => setTaskForm(f => ({ ...f, day_of_month: Number(v) }))}>
                  <SelectTrigger data-testid="task-day-of-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {Array.from({length: 31}, (_, i) => i + 1).map(d => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Months with fewer days fall on the last day — e.g. Day 31 shows on 28 Feb.</p>
              </div>
            )}
            {taskForm.schedule === 'hourly' && (
              <p className="text-xs text-muted-foreground">Hourly tasks appear every day on the kiosk — staff tap them each time the task is done. One daily record is kept for the EHO pack.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskDialog(false)}>Cancel</Button>
            <Button onClick={saveTask} data-testid="save-task-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Count sheet dialog */}
      <Dialog open={sheetDialog} onOpenChange={setSheetDialog}>
        <DialogContent className="max-w-2xl" data-testid="sheet-dialog">
          <DialogHeader>
            <DialogTitle>New count sheet</DialogTitle>
            <DialogDescription>Pick the items to appear in this stock take template.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={sheetForm.name} onChange={(e) => setSheetForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Morning fridge count" data-testid="sheet-name-input" />
            </div>
            <div>
              <Label>Items ({sheetForm.item_ids.length} selected)</Label>
              {inventoryItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">No inventory items yet — connect a POS or import a CSV. You can still save an empty sheet and add items later.</p>
              ) : (
                <div className="max-h-64 overflow-auto border rounded-md divide-y">
                  {inventoryItems.map(item => (
                    <label key={item.variation_external_id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent">
                      <input
                        type="checkbox"
                        checked={sheetForm.item_ids.includes(item.variation_external_id)}
                        onChange={() => toggleSheetItem(item.variation_external_id)}
                        data-testid={`sheet-item-toggle-${item.variation_external_id}`}
                      />
                      <span className="flex-1 text-sm">{item.name}{item.variation_name ? ` · ${item.variation_name}` : ''}</span>
                      <span className="text-xs text-muted-foreground">{item.in_stock ?? '—'} in stock</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSheetDialog(false)}>Cancel</Button>
            <Button onClick={saveSheet} data-testid="save-sheet-btn">Save sheet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
